(function() {
  'use strict';

  var VALID_SORTS = {
    newest: true,
    oldest: true,
    az: true,
    za: true
  };

  function normalize(value) {
    var text = (value || '').toString();

    try {
      text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (error) {
      // String normalization is an enhancement; basic matching still works without it.
    }

    return text
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[’']/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getTokens(value) {
    var normalized = normalize(value);
    return normalized ? normalized.split(' ') : [];
  }

  function tokenMatches(text, token) {
    if (text.indexOf(token) > -1) {
      return true;
    }

    // Let common singular/plural searches match each other without using a fuzzy-search library.
    if (token.length > 3 && token.slice(-1) === 's') {
      return text.indexOf(token.slice(0, -1)) > -1;
    }

    return false;
  }

  function init(root) {
    if (!root || root.dataset.nhInit === 'true') {
      return;
    }

    root.dataset.nhInit = 'true';

    var grid = root.querySelector('[data-articles-grid]');
    if (!grid) {
      return;
    }

    var searchForm = root.querySelector('[data-search-form]');
    var searchInput = root.querySelector('[data-search-input]');
    var clearButton = root.querySelector('[data-clear-search]');
    var sortSelect = root.querySelector('[data-sort-select]');
    var resultSummary = root.querySelector('[data-result-summary]');
    var emptyState = root.querySelector('[data-empty-state]');
    var pagination = root.querySelector('[data-pagination]');
    var pageLinks = Array.prototype.slice.call(root.querySelectorAll('[data-page-link]'));
    var backTopButton = root.querySelector('[data-back-to-top]');
    var initialCards = Array.prototype.slice.call(grid.querySelectorAll('[data-article-card]'));
    var allCards = initialCards.slice();
    var pageCount = parseInt(root.getAttribute('data-page-count') || '1', 10);
    var currentPage = parseInt(root.getAttribute('data-current-page') || '1', 10);
    var totalArticles = parseInt(root.getAttribute('data-total-articles') || String(initialCards.length), 10);
    var blogUrl = root.getAttribute('data-blog-url') || window.location.pathname;
    var updateTimer = null;
    var applyVersion = 0;
    var archivePromise = null;
    var archiveLoaded = pageCount <= 1;
    var archiveFailed = false;
    var collator = null;

    try {
      collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    } catch (error) {
      collator = null;
    }

    function prepareCard(card, fetched) {
      if (!card) {
        return card;
      }

      card._nhSearch = normalize(card.getAttribute('data-search'));
      card._nhTitle = (card.getAttribute('data-title') || '').trim();
      card._nhNormalizedTitle = normalize(card._nhTitle);
      card._nhDate = parseInt(card.getAttribute('data-date') || '0', 10);
      card.hidden = false;

      if (fetched) {
        Array.prototype.forEach.call(card.querySelectorAll('img'), function(image) {
          image.loading = 'lazy';
          image.removeAttribute('fetchpriority');
        });
      }

      return card;
    }

    initialCards.forEach(function(card) {
      prepareCard(card, false);
    });

    function cardKey(card) {
      return (
        card.getAttribute('data-article-url') ||
        card.getAttribute('data-title') + '|' + card.getAttribute('data-date')
      );
    }

    function compareTitles(a, b) {
      if (collator) {
        return collator.compare(a._nhTitle, b._nhTitle);
      }

      return a._nhNormalizedTitle.localeCompare(b._nhNormalizedTitle);
    }

    function sortCards(cards, mode) {
      return cards.sort(function(a, b) {
        var result = 0;

        if (mode === 'oldest') {
          result = a._nhDate - b._nhDate;
        } else if (mode === 'az') {
          result = compareTitles(a, b);
        } else if (mode === 'za') {
          result = compareTitles(b, a);
        } else {
          result = b._nhDate - a._nhDate;
        }

        return result || compareTitles(a, b);
      });
    }

    function renderCards(cards) {
      var fragment = document.createDocumentFragment();

      cards.forEach(function(card) {
        card.hidden = false;
        fragment.appendChild(card);
      });

      while (grid.firstChild) {
        grid.removeChild(grid.firstChild);
      }

      grid.appendChild(fragment);
    }

    function updateUrl(rawQuery, sortMode) {
      if (!window.history || !window.history.replaceState) {
        return;
      }

      var url = new URL(window.location.href);

      if (rawQuery) {
        url.searchParams.set('q', rawQuery);
      } else {
        url.searchParams.delete('q');
      }

      if (sortMode && sortMode !== 'newest') {
        url.searchParams.set('sort', sortMode);
      } else {
        url.searchParams.delete('sort');
      }

      window.history.replaceState({}, '', url.toString());
    }

    function syncStateLinks(rawQuery, sortMode) {
      pageLinks.forEach(function(link) {
        var href = link.getAttribute('href');
        if (!href) {
          return;
        }

        var url = new URL(href, window.location.origin);

        if (rawQuery) {
          url.searchParams.set('q', rawQuery);
        } else {
          url.searchParams.delete('q');
        }

        if (sortMode !== 'newest') {
          url.searchParams.set('sort', sortMode);
        } else {
          url.searchParams.delete('sort');
        }

        link.href = url.toString();
      });
    }

    function setBusy(isBusy) {
      root.classList.toggle('is-loading', isBusy);
      root.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      grid.setAttribute('aria-busy', isBusy ? 'true' : 'false');
    }

    function buildPageUrl(pageNumber) {
      var url = new URL(blogUrl, window.location.origin);

      if (pageNumber > 1) {
        url.searchParams.set('page', String(pageNumber));
      } else {
        url.searchParams.delete('page');
      }

      return url.toString();
    }

    function fetchPage(pageNumber) {
      return fetch(buildPageUrl(pageNumber), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Article page request failed with status ' + response.status);
          }

          return response.text();
        })
        .then(function(html) {
          var parser = new DOMParser();
          var documentFragment = parser.parseFromString(html, 'text/html');
          var fetchedRoot = root.id ? documentFragment.getElementById(root.id) : null;

          if (!fetchedRoot) {
            fetchedRoot = documentFragment.querySelector('[data-news-hub]');
          }

          if (!fetchedRoot) {
            throw new Error('Article archive section was not found in the response.');
          }

          return Array.prototype.slice.call(fetchedRoot.querySelectorAll('[data-article-card]'));
        });
    }

    function loadArchive() {
      if (archiveLoaded || archiveFailed || pageCount <= 1) {
        return Promise.resolve(allCards);
      }

      if (archivePromise) {
        return archivePromise;
      }

      var pages = [];
      var pageNumber;

      for (pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        if (pageNumber !== currentPage) {
          pages.push(pageNumber);
        }
      }

      setBusy(true);

      archivePromise = Promise.all(
        pages.map(function(page) {
          return fetchPage(page);
        })
      )
        .then(function(pageGroups) {
          var seen = {};

          allCards.forEach(function(card) {
            seen[cardKey(card)] = true;
          });

          pageGroups.forEach(function(group) {
            group.forEach(function(card) {
              var importedCard = document.importNode(card, true);
              var key = cardKey(importedCard);

              if (!key || seen[key]) {
                return;
              }

              seen[key] = true;
              allCards.push(prepareCard(importedCard, true));
            });
          });

          archiveLoaded = true;
          root.dataset.archiveLoaded = 'true';
          return allCards;
        })
        .catch(function(error) {
          archiveFailed = true;
          root.dataset.archiveLoadFailed = 'true';

          if (window.console && typeof window.console.warn === 'function') {
            window.console.warn('The full article archive could not be loaded.', error);
          }

          return allCards;
        })
        .then(function(cards) {
          setBusy(false);
          return cards;
        });

      return archivePromise;
    }

    function getState() {
      var rawQuery = searchInput ? searchInput.value.trim() : '';
      var sortMode = sortSelect ? sortSelect.value || 'newest' : 'newest';

      if (!VALID_SORTS[sortMode]) {
        sortMode = 'newest';
      }

      return {
        rawQuery: rawQuery,
        tokens: getTokens(rawQuery),
        sortMode: sortMode,
        altered: rawQuery.length > 0 || sortMode !== 'newest'
      };
    }

    function updateSummary(state, visibleCount) {
      if (!resultSummary) {
        return;
      }

      if (archiveFailed && state.altered) {
        resultSummary.textContent =
          visibleCount +
          (visibleCount === 1 ? ' match on this page. ' : ' matches on this page. ') +
          'The full archive could not be loaded.';
        return;
      }

      if (state.rawQuery) {
        resultSummary.textContent =
          visibleCount + (visibleCount === 1 ? ' article found' : ' articles found');
        return;
      }

      if (state.sortMode !== 'newest') {
        var label = state.sortMode === 'oldest' ? 'oldest first' : state.sortMode === 'az' ? 'A–Z' : 'Z–A';
        resultSummary.textContent = 'Showing ' + visibleCount + ' articles, sorted ' + label;
        return;
      }

      resultSummary.textContent = '';
    }

    function finishApply(state) {
      var sourceCards = state.altered ? allCards.slice() : initialCards.slice();
      var matchingCards = sourceCards.filter(function(card) {
        if (!state.tokens.length) {
          return true;
        }

        return state.tokens.every(function(token) {
          return tokenMatches(card._nhSearch, token);
        });
      });

      if (state.altered) {
        sortCards(matchingCards, state.sortMode);
      }

      renderCards(matchingCards);

      if (clearButton) {
        clearButton.hidden = state.rawQuery.length === 0;
      }

      if (emptyState) {
        emptyState.hidden = matchingCards.length !== 0;
      }

      if (pagination) {
        pagination.hidden = state.altered;
      }

      root.classList.toggle('is-altered', state.altered);
      updateSummary(state, matchingCards.length);
    }

    function apply(skipUrl) {
      var state = getState();
      var version = ++applyVersion;
      var needsArchive = state.altered && pageCount > 1 && !archiveLoaded && !archiveFailed;

      if (clearButton) {
        clearButton.hidden = state.rawQuery.length === 0;
      }

      syncStateLinks(state.rawQuery, state.sortMode);

      if (!skipUrl) {
        updateUrl(state.rawQuery, state.sortMode);
      }

      if (!needsArchive) {
        finishApply(state);
        return;
      }

      if (resultSummary) {
        resultSummary.textContent = 'Searching all ' + totalArticles + ' articles…';
      }

      loadArchive().then(function() {
        if (version !== applyVersion) {
          return;
        }

        finishApply(getState());
      });
    }

    function seedFromUrl() {
      var url = new URL(window.location.href);
      var query = url.searchParams.get('q');
      var sortMode = url.searchParams.get('sort');

      if (searchInput && query) {
        searchInput.value = query;
      }

      if (sortSelect && sortMode && VALID_SORTS[sortMode]) {
        sortSelect.value = sortMode;
      }
    }

    function updateBackTop() {
      if (!backTopButton) {
        return;
      }

      backTopButton.hidden = window.scrollY < 500;
    }

    seedFromUrl();
    apply(true);
    updateBackTop();

    if (searchForm) {
      searchForm.addEventListener('submit', function(event) {
        event.preventDefault();
        window.clearTimeout(updateTimer);
        apply(false);
      });
    }

    if (searchInput) {
      searchInput.addEventListener('input', function() {
        window.clearTimeout(updateTimer);
        updateTimer = window.setTimeout(function() {
          apply(false);
        }, 180);
      });

      searchInput.addEventListener('search', function() {
        window.clearTimeout(updateTimer);
        apply(false);
      });

      searchInput.addEventListener('keydown', function(event) {
        if (event.key === 'Escape' && searchInput.value) {
          event.preventDefault();
          searchInput.value = '';
          apply(false);
        }
      });
    }

    if (clearButton && searchInput) {
      clearButton.addEventListener('click', function() {
        searchInput.value = '';
        searchInput.focus();
        apply(false);
      });
    }

    if (sortSelect) {
      sortSelect.addEventListener('change', function() {
        apply(false);
      });
    }

    if (backTopButton) {
      backTopButton.addEventListener('click', function() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      window.addEventListener('scroll', updateBackTop, { passive: true });
      window.addEventListener('resize', updateBackTop);
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-news-hub]');
    Array.prototype.forEach.call(roots, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function(event) {
    initAll(event.target);
  });
})();
